import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { toast } from 'sonner';
import { Send, Mail, MapPin, Globe2, Phone } from 'lucide-react';

const API_URL = 'https://2t5g376nvl.execute-api.eu-west-2.amazonaws.com/default/esgine-marketing-contact';

export default function Contact() {
  const [form, setForm] = useState({
    name: '',
    email: '',
    company: '',
    role: '',
    jurisdictions: '',
    timeline: '',
    intent: 'proposal',
    context: '',
  });

  const handle = (k) => (e) => setForm({ ...form, [k]: e?.target?.value ?? e });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.company) {
      toast.error('Please fill name, email and company.');
      return;
    }
    try {
      const res = await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error('Submission failed');
      toast.success('Request received. Our team will follow up shortly.');
      setForm({
        name: '', email: '', company: '', role: '',
        jurisdictions: '', timeline: '', intent: 'proposal', context: '',
      });
    } catch (err) {
      toast.error('Something went wrong. Please email compliance@esgine.io directly.');
    }
  };

  return (
    <section id="contact" className="relative bg-[#F2EFE6]">
      <div className="max-w-[1320px] mx-auto px-6 lg:px-10 py-20 lg:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Left */}
          <div className="lg:col-span-5">
            <div className="text-[11.5px] tracking-[0.16em] uppercase text-[#1B6F5E] font-medium">
              Request a proposal
            </div>
            <h2 className="mt-3 font-display text-[40px] lg:text-[56px] leading-[1.02] text-[#0B1F2D]">
              Start a scoped
              <br />
              <span className="italic">conversation.</span>
            </h2>
            <p className="mt-6 text-[16px] leading-[1.6] text-[#0B1F2D]/70 max-w-[480px]">
              Share your reporting context, target regulators and timelines. We'll follow up to propose a scoped pilot or
              annual access plan.
            </p>

            <div className="mt-10 space-y-5">
              {/* Email */}
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg border border-[#E8E5DC] bg-[#FAFAF5] flex items-center justify-center text-[#1B6F5E] flex-shrink-0">
                  <Mail className="w-4 h-4" />
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-[0.12em] text-[#0B1F2D]/55">Email</div>
                  <a href="mailto:compliance@esgine.io" className="text-[14px] text-[#0B1F2D] hover:text-[#1B6F5E] transition-colors">
                    compliance@esgine.io
                  </a>
                </div>
              </div>

              {/* Coverage */}
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg border border-[#E8E5DC] bg-[#FAFAF5] flex items-center justify-center text-[#1B6F5E] flex-shrink-0">
                  <Globe2 className="w-4 h-4" />
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-[0.12em] text-[#0B1F2D]/55">Coverage</div>
                  <div className="text-[14px] text-[#0B1F2D]">EU · US · UK · Global</div>
                </div>
              </div>

              {/* US Office */}
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg border border-[#E8E5DC] bg-[#FAFAF5] flex items-center justify-center text-[#1B6F5E] flex-shrink-0">
                  <MapPin className="w-4 h-4" />
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-[0.12em] text-[#0B1F2D]/55">United States</div>
                  <div className="text-[14px] text-[#0B1F2D] leading-[1.6]">
                    20 F Street NW, #700<br />
                    Washington, DC 20001
                  </div>
                  <a href="tel:+14085036041" className="text-[13px] text-[#1B6F5E] hover:text-[#0B1F2D] transition-colors mt-1 inline-flex items-center gap-1">
                    <Phone className="w-3 h-3" /> +1 408 503 6041
                  </a>
                </div>
              </div>

              {/* UK Office */}
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg border border-[#E8E5DC] bg-[#FAFAF5] flex items-center justify-center text-[#1B6F5E] flex-shrink-0">
                  <MapPin className="w-4 h-4" />
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-[0.12em] text-[#0B1F2D]/55">United Kingdom</div>
                  <div className="text-[14px] text-[#0B1F2D] leading-[1.6]">
                    Peter House, Oxford Street, #408<br />
                    Manchester, M1 5AN
                  </div>
                  <a href="tel:+441613911691" className="text-[13px] text-[#1B6F5E] hover:text-[#0B1F2D] transition-colors mt-1 inline-flex items-center gap-1">
                    <Phone className="w-3 h-3" /> +44 161 391 1691
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Right form */}
          <div className="lg:col-span-7">
            <form
              onSubmit={onSubmit}
              className="rounded-[22px] border border-[#E8E5DC] bg-[#FAFAF5] p-7 lg:p-9 shadow-[0_30px_80px_-32px_rgba(11,31,45,0.12)]"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-[12px] uppercase tracking-[0.1em] text-[#0B1F2D]/60">
                    Full name
                  </Label>
                  <Input id="name" value={form.name} onChange={handle('name')} placeholder="Jane Doe" className="h-11 bg-white border-[#E8E5DC] focus-visible:ring-[#1B6F5E]" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-[12px] uppercase tracking-[0.1em] text-[#0B1F2D]/60">
                    Work email
                  </Label>
                  <Input id="email" type="email" value={form.email} onChange={handle('email')} placeholder="jane@company.com" className="h-11 bg-white border-[#E8E5DC] focus-visible:ring-[#1B6F5E]" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="company" className="text-[12px] uppercase tracking-[0.1em] text-[#0B1F2D]/60">
                    Company
                  </Label>
                  <Input id="company" value={form.company} onChange={handle('company')} placeholder="Company name" className="h-11 bg-white border-[#E8E5DC] focus-visible:ring-[#1B6F5E]" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="role" className="text-[12px] uppercase tracking-[0.1em] text-[#0B1F2D]/60">
                    Role
                  </Label>
                  <Input id="role" value={form.role} onChange={handle('role')} placeholder="e.g., Head of ESG" className="h-11 bg-white border-[#E8E5DC] focus-visible:ring-[#1B6F5E]" />
                </div>
                <div className="space-y-2">
                  <Label className="text-[12px] uppercase tracking-[0.1em] text-[#0B1F2D]/60">Jurisdictions / frameworks</Label>
                  <Input value={form.jurisdictions} onChange={handle('jurisdictions')} placeholder="e.g., CSRD, SEC, SB253" className="h-11 bg-white border-[#E8E5DC] focus-visible:ring-[#1B6F5E]" />
                </div>
                <div className="space-y-2">
                  <Label className="text-[12px] uppercase tracking-[0.1em] text-[#0B1F2D]/60">Filing timeline</Label>
                  <Select value={form.timeline} onValueChange={(v) => setForm({ ...form, timeline: v })}>
                    <SelectTrigger className="h-11 bg-white border-[#E8E5DC] focus:ring-[#1B6F5E]">
                      <SelectValue placeholder="Select timeline" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="q1">Within Q1</SelectItem>
                      <SelectItem value="q2">Within Q2</SelectItem>
                      <SelectItem value="h2">H2 2026</SelectItem>
                      <SelectItem value="2027">2027</SelectItem>
                      <SelectItem value="exploratory">Exploratory</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="mt-6">
                <Label className="text-[12px] uppercase tracking-[0.1em] text-[#0B1F2D]/60">What do you need today?</Label>
                <RadioGroup
                  value={form.intent}
                  onValueChange={(v) => setForm({ ...form, intent: v })}
                  className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3"
                >
                  {[
                    { v: 'proposal', t: 'Request a proposal' },
                    { v: 'briefing', t: 'Book a briefing' },
                  ].map((o) => (
                    <label
                      key={o.v}
                      className={`flex items-center gap-3 px-4 h-12 rounded-xl border bg-white cursor-pointer transition-colors ${
                        form.intent === o.v
                          ? 'border-[#0B1F2D] ring-1 ring-[#0B1F2D]/10'
                          : 'border-[#E8E5DC] hover:border-[#0B1F2D]/40'
                      }`}
                    >
                      <RadioGroupItem value={o.v} id={o.v} className="border-[#0B1F2D]/30 text-[#1B6F5E]" />
                      <span className="text-[14px] text-[#0B1F2D]">{o.t}</span>
                    </label>
                  ))}
                </RadioGroup>
              </div>

              <div className="mt-6 space-y-2">
                <Label htmlFor="context" className="text-[12px] uppercase tracking-[0.1em] text-[#0B1F2D]/60">
                  Context
                </Label>
                <Textarea
                  id="context"
                  value={form.context}
                  onChange={handle('context')}
                  rows={5}
                  placeholder="Describe your ESG/Climate reporting obligations, target frameworks, and how ESGine® can support your process."
                  className="bg-white border-[#E8E5DC] focus-visible:ring-[#1B6F5E]"
                />
              </div>

              <div className="mt-7 flex flex-wrap items-center gap-4">
                <Button
                  type="submit"
                  className="bg-[#0B1F2D] hover:bg-[#0B1F2D]/90 text-[#FAFAF5] rounded-full h-12 px-6 text-[13.5px] inline-flex items-center gap-2"
                >
                  Send request <Send className="w-3.5 h-3.5" />
                </Button>
                <span className="text-[12px] text-[#0B1F2D]/55">
                  This form sends your request securely to our team.
                </span>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
